import { useQueryClient } from '@tanstack/react-query';
import * as R from 'ramda';
import { useCallback } from 'react';
import { DataQueryKey } from '../../../hooks/useDataQuery/dataQuery.types';
import useHandleAppException from '../../../hooks/useHandleAppException';
import LangEnum from '../../../lang/LangEnum';
import snackbarStore from '../../../stores/Snackbar.store';
import { useApplicationContext } from '../../context/ApplicationContext/ApplicationContext';
import { useLoadingStatusStore } from '../../context/loadingStatus/LoadingStatus.store';
import { AvailabilityEnum } from '../../products/ProductTypes';
import { useTranslation } from '../../translation/TranslationContext';
import { validateOrderProductsQuery } from '../contexts/order.utils';
import { SavedStackItem, StackItem } from '../interfaces/use-order-item-stack.types';
import useRelatedMaterials from '../products/product/grid/components/orderProductGrid/hooks/useRelatedMaterials';
import { ordersRepository } from '../repositories/ordersRepository/orders.repository';
import { syncSavedItemsIntoGridQueries } from '../utils/orderCalculate.utils';

interface UseOrderItemGridUpdateParams {
  afterUpdate: (items: SavedStackItem[]) => void;
}

const { handleMessage } = snackbarStore;

export const useOrderItemGridUpdate = ({ afterUpdate }: UseOrderItemGridUpdateParams) => {
  const queryClient = useQueryClient();
  const { translate } = useTranslation();
  const { handleErrorResponse } = useHandleAppException();
  const { team } = useApplicationContext();
  const { refetchProductColorsSharingMaterial } = useRelatedMaterials();
  const handleLoadingStatus = useLoadingStatusStore((state) => state.handleLoadingStatus);

  const handleError = useCallback(
    (err: unknown) => {
      if (err instanceof Error) {
        const error = JSON.parse(err.message);
        const messageCode = error?.code || error.data?.code;

        const message = handleErrorResponse({ ok: false, data: { ...error, code: messageCode } });

        handleMessage({
          type: 'error',
          message,
        });
        return;
      }

      handleMessage({
        type: 'error',
        message: translate(LangEnum.ERROR_UPDATING_ITEM_MESSAGE),
      });
    },
    [handleErrorResponse, translate],
  );

  const refetchProductColorsSharingMaterialsIfNecessary = useCallback(
    async (newStack: SavedStackItem[]) => {
      const mustRefetchRelatedProductColors = newStack.some(
        (stackItem) => stackItem.availability === AvailabilityEnum.MATERIAL_PRE_ORDER,
      );

      if (mustRefetchRelatedProductColors) {
        const orderId = newStack[0].orderId;
        const productColorIds = newStack.map(({ productColorId }) => productColorId);
        refetchProductColorsSharingMaterial(orderId, productColorIds);
      }
    },
    [refetchProductColorsSharingMaterial],
  );

  const updateOrderItems = useCallback(
    async (items: StackItem[]) => {
      const groupedStack = R.groupBy<StackItem>(({ orderId }) => orderId, items);
      const pairs = R.toPairs(groupedStack);

      const newStack: SavedStackItem[] = [];

      for await (const [orderId, currentStack] of pairs) {
        const repository = ordersRepository(orderId);

        try {
          const response = await handleLoadingStatus({
            requestFn: () => repository.createOrUpdateOrderList(currentStack ?? []),
            onSuccess: async () => {
              await queryClient.refetchQueries(validateOrderProductsQuery(orderId, team?.id));
            },
          });
          if (response.ok && !R.isNil(response.data)) {
            newStack.push(
              ...response.data.map((item) => ({ ...item, orderId, productColorId: item.color.id })),
            );
            refetchProductColorsSharingMaterialsIfNecessary(newStack);
          }
        } catch (err) {
          handleError(err);
        }
      }

      syncSavedItemsIntoGridQueries({ queryClient, savedItems: newStack, teamId: team?.id });

      const savedProductColorIds = R.uniq(
        newStack.map((savedItem) => savedItem.productColorId).filter(Boolean),
      );
      savedProductColorIds.forEach((productColorId) => {
        queryClient.invalidateQueries({
          queryKey: [team?.id, DataQueryKey.PRODUCT_GRID, productColorId],
        });
      });

      afterUpdate(newStack);
    },
    [
      refetchProductColorsSharingMaterialsIfNecessary,
      handleLoadingStatus,
      afterUpdate,
      team?.id,
      handleError,
      queryClient,
      queryClient.refetchQueries,
      queryClient.invalidateQueries,
    ],
  );

  return {
    updateOrderItems,
  };
};
